import {Box, Divider, Stack, Typography} from '@mui/material';
import React, {Fragment} from 'react';

import {NetworkEnum} from 'main/analytics/gaTagsEnum';
import {Snackbar} from 'presentation/components';
import {PhonesDrawer} from 'presentation/components/PhonesDrawer';
import {PhonesMenu} from 'presentation/components/PhonesMenu';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';

import {
	StyledCard,
	StyledOptionsContainer,
	StyledQualificationsContainer,
	StyledSmallButton,
	StyledTitleWithQualifications,
} from './styles';
import {OdontoGuideCardProps} from './types';

import {QualificationList} from 'presentation/modules/guide/shared/ui/components';
import {RenderAddressField} from 'presentation/modules/guide/shared/ui/components/RenderAddressField';
import {OdontoGuideDetails} from '../OdontoGuideDetails';
import {useOdontoGuideCard} from './hooks/useOdontoGuideCard';

const {showDetails, call, fromMe} = translations['pt-br'].commons;
const {emptyPhone, choosePhoneNumber} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;

export function OdontoGuideCard({provider}: OdontoGuideCardProps) {
	const {
		shouldShowQualifications,
		address,
		phoneNumbers,
		isPhoneNumberMenuOpen,
		showPhonesDrawer,
		hasPhoneNumbers,
		handleOpenDetails,
		handleOpenPhoneMenu,
		openDetails,
		setOpenDetails,
		openPhoneDrawerState,
		setOpenPhoneDrawerState,
		anchorEl,
		setAnchorEl,
		showEmptyPhoneSnackbar,
		setShowEmptyPhoneSnackbar,
		selectedProviderState,
		theme,
	} = useOdontoGuideCard(provider);

	return (
		<Fragment>
			<StyledCard elevation={0}>
				<Stack sx={{padding: 2, gap: 1}}>
					<StyledQualificationsContainer>
						<StyledTitleWithQualifications variant="subtitle1">
							{formatText(
								provider.nomeFantasia ? provider.nomeFantasia : provider.nome,
							)}
						</StyledTitleWithQualifications>

						{shouldShowQualifications ? (
							<QualificationList qualifications={provider.qualificacoes} />
						) : null}
					</StyledQualificationsContainer>

					<RenderAddressField
						formattedAddress={address?.formattedAddress}
						addressForMaps={address?.addressForMaps}
					/>
					{provider?.distancia > 0 ? (
						<Stack direction="row" gap={1}>
							<Stack minWidth={20}>
								<Icon
									name={'location-on-filled'}
									size={20}
									color={theme.palette.primary.main}
								/>
							</Stack>
							<Typography variant="body2" color="text.secondary">
								{provider.distancia?.toFixed(1)}km {fromMe}
							</Typography>
						</Stack>
					) : undefined}
				</Stack>
				<Box>
					<Divider />
					<StyledOptionsContainer py={1} px={2}>
						<StyledSmallButton
							onClick={handleOpenDetails}
							size="small"
							variant="outlined"
							data-tag-id={NetworkEnum.CLICK_VER_DETALHES}>
							{showDetails}
						</StyledSmallButton>

						<StyledSmallButton
							onClick={handleOpenPhoneMenu}
							size="small"
							variant="contained"
							data-tag-id={NetworkEnum.CLICK_VER_CONTATO_REDE_CREDENCIADA}>
							{call}
						</StyledSmallButton>
					</StyledOptionsContainer>
				</Box>
			</StyledCard>

			<OdontoGuideDetails
				open={openDetails}
				setOpen={setOpenDetails}
				provider={selectedProviderState}
			/>

			{hasPhoneNumbers ? (
				<PhonesMenu.Root
					open={isPhoneNumberMenuOpen}
					onClose={() => setAnchorEl(null)}
					anchorEl={anchorEl}>
					<PhonesMenu.Simple phoneNumbers={phoneNumbers} />
				</PhonesMenu.Root>
			) : (
				<Snackbar
					onClose={() => setShowEmptyPhoneSnackbar(false)}
					message={emptyPhone}
					open={showEmptyPhoneSnackbar}
				/>
			)}

			{showPhonesDrawer ? (
				<PhonesDrawer.Root
					open={openPhoneDrawerState}
					toggleDrawer={() => setOpenPhoneDrawerState(prev => !prev)}
					title={choosePhoneNumber}>
					<PhonesDrawer.Simple phoneNumbers={phoneNumbers} />
				</PhonesDrawer.Root>
			) : null}
		</Fragment>
	);
}
